import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const Packages = () => {
  const { t } = useLanguage();

  return (
    <section id="pakete" className="bg-dark py-20 lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-16">
        <Reveal>
          <Eyebrow>{t.packages.eyebrow}</Eyebrow>
        </Reveal>
        <div className="mt-4 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <Reveal delay={0.08}>
            <h2 className="max-w-xl font-display text-4xl font-medium tracking-tight sm:text-5xl">{t.packages.title}</h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="max-w-md text-base leading-relaxed text-inkmuted">{t.packages.sub}</p>
          </Reveal>
        </div>

        <motion.div
          data-testid="audit-package-card"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -4 }}
          className="mt-14 flex flex-col items-start gap-8 rounded-[3px] border-2 border-gold bg-goldsoft p-8 sm:flex-row sm:items-center lg:p-10"
        >
          <div className="flex-1">
            <span data-testid="audit-package-badge" className="inline-flex items-center gap-1.5 rounded-full bg-gold px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-ink">
              <ShieldCheck size={10} /> {t.auditPackage.badge}
            </span>
            <h3 className="mt-4 font-display text-3xl font-medium tracking-tight">{t.auditPackage.title}</h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-inkmuted sm:text-base">{t.auditPackage.copy}</p>
          </div>
          <div className="flex flex-col items-start gap-4 sm:items-end">
            <div className="sm:text-right">
              <p className="font-display text-4xl font-medium tracking-tight text-gold">{t.auditPackage.price}</p>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{t.auditPackage.unit}</p>
            </div>
            <button
              data-testid="audit-package-cta"
              onClick={() => {
                window.dispatchEvent(new CustomEvent("joudi-select-service", { detail: "Audits & Loss Prevention" }));
                scrollToId("kontakt");
              }}
              className="group inline-flex items-center gap-2 rounded-[3px] bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
            >
              {t.auditPackage.cta}
              <ArrowUpRight size={15} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </div>
        </motion.div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t.packages.cards.map((p, i) => {
            const isEnterprise = p.id === "enterprise";
            const isPopular = p.id === "sichtbarkeit";
            return (
              <motion.div
                key={p.id}
                data-testid={`package-card-${p.id}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className={`flex h-full flex-col rounded-[3px] p-8 ${
                  isEnterprise
                    ? "bg-navy text-bone"
                    : isPopular
                      ? "border-2 border-gold bg-goldsoft lg:shadow-[0_24px_60px_rgba(61,158,255,0.14)]"
                      : "border border-bone/10 bg-carddark"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-gold">[ 0{i + 1} ]</span>
                  {isPopular && (
                    <span
                      data-testid="package-badge-popular"
                      className="inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-ink"
                    >
                      <Sparkles size={10} /> {t.packages.popular}
                    </span>
                  )}
                </div>
                <h3 className="mt-4 min-h-[64px] font-display text-2xl font-medium text-bone">{p.name}</h3>
                <p className={`mt-1 text-sm ${isEnterprise ? "text-bone/60" : "text-inkmuted"}`}>{p.tag}</p>

                <div className="mt-8 flex items-baseline gap-2">
                  <span className={`font-display text-4xl font-medium tracking-tight text-bone ${p.price === "VB" ? "italic" : ""}`}>
                    {p.price}
                  </span>
                </div>
                <span className={`mt-1 font-mono text-xs uppercase tracking-[0.2em] ${isEnterprise ? "text-gold" : "text-inkmuted"}`}>
                  {p.unit}
                </span>

                <ul className={`mt-7 flex-1 space-y-3 border-t pt-6 ${isEnterprise ? "border-bone/15" : "border-bone/10"}`}>
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm leading-snug ${isEnterprise ? "text-bone/80" : "text-bone/75"}`}>
                      <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`package-cta-${p.id}`}
                  onClick={() => scrollToId("kontakt")}
                  className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-[3px] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                    isEnterprise || isPopular
                      ? "bg-gold text-ink hover:bg-bone"
                      : "border border-bone/20 hover:border-gold hover:bg-gold hover:text-ink"
                  }`}
                >
                  {isEnterprise ? t.packages.enterpriseCta : t.packages.cta}
                  <ArrowUpRight size={15} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </button>
              </motion.div>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 font-mono text-xs leading-relaxed tracking-wide text-inkmuted">{t.packages.note}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default Packages;
